import { useFlags } from "launchdarkly-react-client-sdk";
import { forwardRef, useCallback, useImperativeHandle, useMemo } from "react";
import { Control, useWatch } from "react-hook-form";

import {
  EmailEditorTab,
  EmailEditorTabs,
  useEmailEditorTabs
} from "components/email-builder";
import { EMAIL_BUILDER_MERGE_TAGS } from "components/email-builder/email-builder-merge-tags";
import InputFormField from "components/input-form-field";
import WordProcessorField from "components/word-processor-field";
import {
  DRAG_AND_DROP_EMAIL_BUILDER,
  TOUCHPOINTS_WORDPROCESSOR_FIELD
} from "constants/feature-flags";
import {
  COMMUNICATION_METHOD_EMAIL,
  COMMUNICATION_METHOD_TEXT_SMS
} from "constants/patients";
import {
  EMAIL_TEMPLATE_DESIGN_ONLY_TYPES,
  EMAIL_TEMPLATE_WITH_DESIGN_TYPES,
  MESSAGE_TEMPLATE_TYPES,
  TEMPLATE_TYPE_DESIGNER_EMAIL,
  TEXT_MESSAGE_BACKEND_MAX_LENGTH
} from "constants/templates";
import { type TextTemplate } from "services/types/text-templates.type";
import { getTextTemplateSubjectValue } from "utils/message-templates.util";

import MessageLabelWithWarning from "./message-label-with-warning.component";
import { NewTouchpointFormValues } from "./new-touchpoint-form";

type TouchpointMessageFieldProps = {
  height?: number;
  control: Control<NewTouchpointFormValues>;
  communicationMethod: string | number | null;
  onMessageUpdate?: (html: string) => void;
  onSubjectUpdate?: (subject: string) => void;
};

export type TouchpointMessageFieldRef = {
  exportHtml: () => Promise<{ html: string; design: UnlayerDesign } | null>;
  isEmailBuilder: boolean;
};

const TouchpointMessageField = forwardRef<
  TouchpointMessageFieldRef,
  TouchpointMessageFieldProps
>(
  (
    { height, control, communicationMethod, onMessageUpdate, onSubjectUpdate },
    ref
  ) => {
  const flags = useFlags();
  const isTouchpointsWordProcessorField =
    flags[TOUCHPOINTS_WORDPROCESSOR_FIELD];
  const isDragAndDropEmailEnabled = flags[DRAG_AND_DROP_EMAIL_BUILDER];
  const patient = useWatch({ control, name: "patient" });

  const isTextOrSms =
    Number(communicationMethod) === COMMUNICATION_METHOD_TEXT_SMS;
  const isEmail = Number(communicationMethod) === COMMUNICATION_METHOD_EMAIL;

  const templateTypes = useMemo(() => {
    return isTextOrSms
      ? MESSAGE_TEMPLATE_TYPES
      : EMAIL_TEMPLATE_WITH_DESIGN_TYPES;
  }, [isTextOrSms]);

  // Email builder tabs state
  const {
    emailBuilderRef,
    activeTab,
    setActiveTab,
    isEmailBuilder,
    exportHtml: exportHtmlFromBuilder,
    loadDesign,
    setActiveDesign,
    activeDesign,
    editorTabIsDisabled,
    setEditorTabIsDisabled
  } = useEmailEditorTabs();

  // Expose exportHtml method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      exportHtml: async () => {
        if (!isEmailBuilder || !isDragAndDropEmailEnabled) {
          return null;
        }
        try {
          return await exportHtmlFromBuilder();
        } catch (error) {
          console.error("Failed to export email builder HTML:", error);
          return null;
        }
      },
      isEmailBuilder: isEmailBuilder && isDragAndDropEmailEnabled
    }),
    [isEmailBuilder, exportHtmlFromBuilder, isDragAndDropEmailEnabled]
  );

  const handleTemplateInserted = useCallback(
    (template: TextTemplate | null) => {
      const subject = getTextTemplateSubjectValue(template?.subject_template);
      if (subject) {
        onSubjectUpdate?.(subject);
      }

      if (
        template?.type === TEMPLATE_TYPE_DESIGNER_EMAIL &&
        template?.email_design_json
      ) {
        loadDesign(template.email_design_json);
      }
    },
    [loadDesign, onSubjectUpdate]
  );

  if (isTouchpointsWordProcessorField && (isTextOrSms || isEmail)) {
    const wordProcessorField = (
      <WordProcessorField
        name="message"
        control={control}
        placeholder=""
        featuresGroup={isTextOrSms ? "textOnly" : "light"}
        allowTemplates={isTextOrSms || (isEmail && isDragAndDropEmailEnabled)}
        allowMergeTags
        allowInsertMergeTagsValues
        allowToolbarAutohide={false}
        allowImageAutoUpload={!isTextOrSms}
        maxLength={isTextOrSms ? TEXT_MESSAGE_BACKEND_MAX_LENGTH : undefined}
        templateTypes={templateTypes}
        autoFocus
        required={
          activeTab !== EmailEditorTab.EmailBuilder &&
          (isTextOrSms || (isEmail && !isEmailBuilder))
        }
        mergeTagsPatientId={patient?.id}
        height={height}
        allowImages={isEmail}
        maxImageSizeMB={2}
        onTemplateInserted={isEmail ? handleTemplateInserted : undefined}
      />
    );

    return (
      <>
        <MessageLabelWithWarning />
        {isEmail && isDragAndDropEmailEnabled ? (
          <EmailEditorTabs
            emailBuilderRef={emailBuilderRef}
            initialDesign={activeDesign}
            setActiveDesign={setActiveDesign}
            showTabs={true}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            editorTabIsDisabled={editorTabIsDisabled}
            setEditorTabIsDisabled={setEditorTabIsDisabled}
            templateTypes={EMAIL_TEMPLATE_DESIGN_ONLY_TYPES}
            onExportFromBuilder={(html) => {
              onMessageUpdate?.(html);
            }}
            editorContent={wordProcessorField}
            mergeTags={EMAIL_BUILDER_MERGE_TAGS}
          />
        ) : (
          wordProcessorField
        )}
      </>
    );
  }

  return (
    <InputFormField
      name="message"
      label={<MessageLabelWithWarning />}
      control={control}
      multiline
      sx={{ ml: 0 }}
      required
    />
  );
});

TouchpointMessageField.displayName = "TouchpointMessageField";

export default TouchpointMessageField;
